<script setup lang="ts">
import { IonApp, IonRouterOutlet } from '@ionic/vue'
import { onMounted, onUnmounted, ref } from 'vue'
import { useSettingsStore } from '@/stores/settings'
import { useAdminStore } from '@/stores/admin'
import { useUserStore } from '@/stores/user'
import { useNotificationSettingsStore } from '@/stores/notification-settings'
import { useKeepAwake } from '@/composables/useKeepAwake'
import { PushNotificationService } from '@/services/notifications/PushNotificationService'
import { cleanupOldAttendanceStatus } from '@/services/attendance-status-sync'
import { refreshTokenIfNeeded } from '@/services/auth/auth-service'
import { LocalNotifications } from '@capacitor/local-notifications'
import { Capacitor } from '@capacitor/core'
import AppUpdateModal from '@/components/common/AppUpdateModal.vue'
import appUpdateService, { type UpdateInfo } from '@/services/update/app-update'

const settingsStore = useSettingsStore()
const adminStore = useAdminStore()
const userStore = useUserStore()
const notificationSettingsStore = useNotificationSettingsStore()

// Keep screen awake to prevent automatic locking
useKeepAwake()

// App update state
const showUpdateModal = ref(false)
const updateInfo = ref<UpdateInfo | null>(null)
const webUpdateAvailable = ref(false)
const nativeUpdateAvailable = ref(false)

// Listen for update check results
const unsubscribeUpdate = appUpdateService.addUpdateListener((result) => {
    if (result.webUpdateAvailable || result.nativeUpdateAvailable) {
        updateInfo.value = result.updateInfo
        webUpdateAvailable.value = result.webUpdateAvailable
        nativeUpdateAvailable.value = result.nativeUpdateAvailable
        showUpdateModal.value = true
    }
})

onUnmounted(() => {
    unsubscribeUpdate()
})

/**
 * Cancel all existing local notifications on startup
 * This clears out any old local notifications from before the migration to server notifications
 */
async function cancelAllLocalNotifications() {
    // Only run on native platforms (not web)
    if (Capacitor.getPlatform() === 'web') {
        return
    }

    try {
        // Get all pending local notifications
        const pending = await LocalNotifications.getPending()

        if (pending.notifications && pending.notifications.length > 0) {
            // Exclude the auto BLE check-in notification (ID 9001) — it may fire right after app opens
            const toCancel = pending.notifications.filter(n => n.id !== 9001)

            if (toCancel.length === 0) {
                console.log('[App] No local notifications to cancel (only auto-BLE reserved IDs found)')
                return
            }

            console.log(`[App] Canceling ${toCancel.length} pending local notifications`)

            await LocalNotifications.cancel({
                notifications: toCancel
            })

            console.log('[App] All local notifications canceled successfully')
        } else {
            console.log('[App] No pending local notifications found')
        }
    } catch (error) {
        // Not critical - don't block app startup
        console.warn('[App] Failed to cancel local notifications:', error)
    }
}

/**
 * Force ensure safe area insets are available for CSS
 */
function ensureSafeAreaInsets() {
    // Wait for Capacitor platform to be ready
    if (Capacitor.isNativePlatform()) {
        // Give the native platform time to set safe area insets
        setTimeout(() => {
            // Force a reflow to ensure CSS variables are recalculated
            document.documentElement.style.setProperty('--safe-area-check', '1')
            console.log('[App] Safe area insets initialized')
        }, 100)
    }
}

/**
 * Initialize app on mount
 * Handles: config loading, push notifications, store initialization
 */
onMounted(async () => {
    // Ensure safe area insets are available
    ensureSafeAreaInsets()

    try {
        // Cancel all old local notifications first (cleanup from before server notification migration)
        await cancelAllLocalNotifications()

        // Initialize admin store to load debug settings
        await adminStore.initializeAuth()

        // Refresh auth token if nearing expiry (prevents "forgotten user" after days)
        try {
            await refreshTokenIfNeeded()
        } catch (error) {
            console.warn('[App] Failed to refresh token on startup:', error)
        }

        // Load cached config immediately for fast startup
        await settingsStore.loadCachedConfig()

        // Then refresh from API if cache is expired or to get latest
        // This will skip if user is not authenticated
        try {
            await settingsStore.loadConfig()
        } catch (configError) {
            // Config loading failed - not critical for unauthenticated users
            // They will be redirected to ProfileAssignment anyway
            console.warn('[App] Failed to load config (user may not be authenticated):', configError)
        }

        // Initialize push notifications after config is loaded
        // Use setTimeout to ensure Vue is fully initialized (fixes Android timing issues)
        // NOTE: Permission request happens in Dashboard after worker assignment for better UX
        // NOTE: Device token registration happens in Dashboard after profile is loaded
        setTimeout(async () => {
            try {
                // Only initialize if a worker is already assigned (returning users)
                // For first-time users, initialization happens in Dashboard after assignment
                if (userStore.workerId) {
                    console.log('[App] Worker assigned, initializing push notifications')
                    await PushNotificationService.initialize()

                    // Register callback for foreground notifications
                    PushNotificationService.onNotificationReceived(async (notification) => {
                        console.log('[App] Push notification received in foreground:', notification)

                        // Show local notification when push arrives in foreground
                        // (Android doesn't show system notifications for foreground push by default)
                        try {
                            await LocalNotifications.schedule({
                                notifications: [
                                    {
                                        id: Math.floor(Math.random() * 100000),
                                        title: notification.title || 'Notification',
                                        body: notification.body || '',
                                        schedule: { at: new Date(Date.now() + 100) }, // Show in 100ms
                                        sound: undefined,
                                        attachments: undefined,
                                        actionTypeId: '',
                                        extra: notification.data
                                    }
                                ]
                            })
                            console.log('[App] Local notification scheduled for foreground push')
                        } catch (error) {
                            console.warn('[App] Failed to show local notification:', error)
                        }
                    })

                    // Register callback for notification actions (user taps notification)
                    PushNotificationService.onNotificationAction((action) => {
                        console.log('[App] Push notification action performed:', action)
                        // Handle notification tap - could navigate to specific screen based on notification data
                        // For now, just log it
                    })

                    // Load notification preferences from cache (will be refreshed in Dashboard)
                    await notificationSettingsStore.loadSettings()

                    console.log('[App] Push notifications initialized successfully with callbacks')
                } else {
                    console.log('[App] No worker assigned yet, skipping push notification initialization')
                }
            } catch (notifError) {
                // Notification initialization is not critical - don't block app startup
                console.warn('[App] Failed to initialize push notifications:', notifError)
            }
        }, 500) // 500ms delay to ensure i18n and Vue are fully initialized

        // Cleanup old attendance status entries
        try {
            await cleanupOldAttendanceStatus()
        } catch (cleanupError) {
            console.warn('[App] Failed to cleanup old attendance status:', cleanupError)
        }

        // Check for app updates (non-blocking, runs in background)
        appUpdateService.checkForUpdates().catch(error => {
            console.warn('[App] Failed to check for updates on startup:', error)
        })
    } catch (error) {
        console.error('[App] Failed to initialize app', error)
    }
})
</script>

<template>
    <IonApp>
        <IonRouterOutlet />
        <AppUpdateModal
            :is-open="showUpdateModal"
            :update-info="updateInfo"
            :web-update-available="webUpdateAvailable"
            :native-update-available="nativeUpdateAvailable"
            @dismiss="showUpdateModal = false"
        />
    </IonApp>
</template>
